import { Plain } from './plain';

export namespace Rich {
    export class DateTime implements Plain.DateTime {
        readonly date: SimpleDate;
        readonly time: TimeOfDay;

        get plain(): Plain.DateTime {
            return { date: this.date.plain, time: this.time.plain };
        }

        static from(plain: Plain.DateTime): DateTime {
            return plain instanceof DateTime ? plain : new DateTime(plain);
        }

        constructor(date: Date);
        constructor(plain: Plain.DateTime);
        constructor(params: Plain.DateTime | Date) {
            let date: SimpleDate;
            let time: TimeOfDay;
            if (params instanceof Date) {
                date = new SimpleDate(params);
                time = new TimeOfDay(params);
            } else {
                const { date: _date, time: _time } = params;
                date = SimpleDate.from(_date);
                time = TimeOfDay.from(_time);
            }

            const totalMinutes = time.minutes;
            const minutesPerDay = 24 * 60;
            // adjust time overflow
            if (totalMinutes >= minutesPerDay) {
                time = new TimeOfDay({ minutes: totalMinutes % minutesPerDay });
                date = date.adding({ days: Math.floor(totalMinutes / minutesPerDay) });
            }

            this.time = time;
            this.date = date;
        }

        isEqualTo(other: Plain.DateTime): boolean {
            return this.compareTo(other) === 0;
        }

        compareTo(other: Plain.DateTime): number {
            const dateDiff = this.date.compareTo(other.date);
            if (dateDiff !== 0) return dateDiff;
            return this.time.compareTo(other.time);
        }

        adding(params: { minutes?: number; hours?: number; days?: number }): DateTime {
            const totalMinutes =
                (params.minutes ?? 0) +
                (params.hours ? params.hours * 60 : 0) +
                (params.days ? params.days * 24 * 60 : 0);

            const newTotalMinutes = this.time.minutes + totalMinutes;
            const minutesPerDay = 24 * 60;
            const daysOverflow = Math.floor(newTotalMinutes / minutesPerDay);
            const remainingMinutes = ((newTotalMinutes % minutesPerDay) + minutesPerDay) % minutesPerDay;

            const newDate = daysOverflow !== 0 ? this.date.adding({ days: daysOverflow }) : this.date;
            const newTime = new TimeOfDay({ minutes: remainingMinutes });

            return new DateTime({ date: newDate, time: newTime });
        }

        minutesUntil(other: DateTime | Plain.DateTime): number {
            const otherTime = other instanceof DateTime ? other.time : new TimeOfDay(other.time);
            const thisMinutes = this.date.daysUntil(other.date) * 24 * 60 + otherTime.minutes - this.time.minutes;
            return thisMinutes;
        }

        hoursUntil(other: DateTime | Plain.DateTime): number {
            return this.minutesUntil(other) / 60;
        }

        toDate(): Date {
            return new Date(this.date.year, this.date.month - 1, this.date.day, this.time.hour, this.time.minute);
        }
    }

    export class SimpleDate implements Plain.SimpleDate {
        readonly day: number;
        readonly month: number;
        readonly year: number;

        get plain(): Plain.SimpleDate {
            return { day: this.day, month: this.month, year: this.year };
        }

        static from(plain: Plain.SimpleDate): SimpleDate {
            return plain instanceof SimpleDate ? plain : new SimpleDate(plain);
        }

        static fromISOString(isoDate: string): SimpleDate {
            return new SimpleDate({
                year: parseInt(isoDate.slice(0, 4)),
                month: parseInt(isoDate.slice(5, 7)),
                day: parseInt(isoDate.slice(8, 10)),
            });
        }

        toISOString(): string {
            return `${this.year}-${String(this.month).padStart(2, '0')}-${String(this.day).padStart(2, '0')}`;
        }

        constructor(date: Date);
        constructor(plain: Plain.SimpleDate);
        constructor(params: Plain.SimpleDate | Date) {
            if (params instanceof Date) {
                this.day = params.getDate();
                this.month = params.getMonth() + 1;
                this.year = params.getFullYear();
            } else {
                this.day = params.day;
                this.month = params.month;
                this.year = params.year;
            }
        }

        isEqualTo(other: Plain.SimpleDate): boolean {
            return this.compareTo(other) === 0;
        }

        isAfter(other: Plain.SimpleDate): boolean {
            return this.compareTo(other) > 0;
        }

        isBefore(other: Plain.SimpleDate): boolean {
            return this.compareTo(other) < 0;
        }

        adding(params: { days: number }): SimpleDate {
            const date = this.toDate();
            date.setDate(date.getDate() + params.days);
            return new SimpleDate(date);
        }

        daysUntil(other: Plain.SimpleDate): number {
            const thisDate = this.toDate();
            const otherDate = SimpleDate.from(other).toDate();
            const diffTime = otherDate.getTime() - thisDate.getTime();
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }

        weekday(): Weekday {
            return Weekday.fromIndex(this.toDate().getDay());
        }

        get isWeekend(): boolean {
            const weekday = this.toDate().getDay();
            return weekday === 0 || weekday === 6;
        }

        compareTo(other: Plain.SimpleDate): number {
            const { year, month, day } = other;
            if (this.year !== year) return this.year - year;
            if (this.month !== month) return this.month - month;
            return this.day - day;
        }

        toDate(): Date {
            return new Date(this.year, this.month - 1, this.day);
        }
    }

    export class Month implements Plain.Month {
        readonly month: number;
        readonly year: number;

        get plain(): Plain.Month {
            return { month: this.month, year: this.year };
        }

        static from(plain: Plain.Month): Month {
            return plain instanceof Month ? plain : new Month(plain);
        }

        constructor(date: Date);
        constructor(plain: Plain.Month);
        constructor(params: Date | Plain.Month) {
            if (params instanceof Date) {
                this.month = params.getMonth() + 1;
                this.year = params.getFullYear();
            } else {
                this.month = params.month;
                this.year = params.year;
            }
        }

        adding(params: { months: number }): Month {
            // Go through JS Date so month/year rollover normalizes; day=1 is always valid in every
            // month so there's no day-overflow concern here.
            const date = new Date(this.year, this.month - 1 + params.months, 1);
            return new Month({ month: date.getMonth() + 1, year: date.getFullYear() });
        }

        // The last day of this calendar month (28–31). day=0 of the next month is the last of this.
        get lastDay(): number {
            return new Date(this.year, this.month, 0).getDate();
        }

        // Midnight of day 1, in the local timezone — matches how users perceive
        // month boundaries in the UI and how naive TIMESTAMP columns are stored.
        get startDate(): Date {
            return new Date(this.year, this.month - 1, 1);
        }

        // Builds a SimpleDate in this month, clamping day to the last valid day
        // (e.g. day=31 in Feb → 28 or 29).
        dateClampingDay(day: number): SimpleDate {
            return new SimpleDate({ year: this.year, month: this.month, day: Math.min(day, this.lastDay) });
        }

        isAfter(other: Plain.Month): boolean {
            return this.year > other.year || (this.year === other.year && this.month > other.month);
        }
    }

    export class TimeOfDay implements Plain.TimeOfDay {
        static startOfDay = new TimeOfDay({ hour: 0, minute: 0 });
        static endOfDay = this.startOfDay.adding({ days: 1 });

        static from(plain: Plain.TimeOfDay): TimeOfDay {
            return plain instanceof TimeOfDay ? plain : new TimeOfDay(plain);
        }

        readonly minutes: number;

        get hour(): number {
            return Math.floor(this.minutes / 60);
        }

        get minute(): number {
            return this.minutes % 60;
        }

        get plain(): Plain.TimeOfDay {
            return { hour: this.hour, minute: this.minute };
        }

        constructor(date: Date);
        constructor(plain: Plain.TimeOfDay);
        constructor(params: { minutes: number });
        constructor(params: Date | { minutes: number } | Plain.TimeOfDay) {
            if (params instanceof Date) {
                this.minutes = params.getHours() * 60 + params.getMinutes();
            } else if ('minutes' in params) {
                this.minutes = params.minutes;
            } else {
                this.minutes = params.hour * 60 + params.minute;
            }
        }

        adding(params: { minutes?: number; hours?: number; days?: number }): TimeOfDay {
            const minutes =
                (params.minutes ?? 0) +
                (params.hours ? params.hours * 60 : 0) +
                (params.days ? params.days * 60 * 24 : 0);

            return new TimeOfDay({ minutes: this.minutes + minutes });
        }

        compareTo(other: Plain.TimeOfDay): number {
            return this.minutes - TimeOfDay.from(other).minutes;
        }

        distanceTo(other: Plain.TimeOfDay): { minutes: number } {
            return { minutes: TimeOfDay.from(other).minutes - this.minutes };
        }

        isBefore(other: Plain.TimeOfDay): boolean {
            return this.compareTo(other) < 0;
        }

        isBeforeOrEqualTo(other: Plain.TimeOfDay): boolean {
            return this.compareTo(other) <= 0;
        }

        isAfter(other: Plain.TimeOfDay): boolean {
            return this.compareTo(other) > 0;
        }

        isAfterOrEqualTo(other: Plain.TimeOfDay): boolean {
            return this.compareTo(other) >= 0;
        }

        isEqualTo(other: Plain.TimeOfDay): boolean {
            return this.compareTo(other) === 0;
        }
    }

    export class DayTimeSlice implements Plain.DayTimeSlice {
        readonly start: TimeOfDay;
        readonly end: TimeOfDay;

        get minutes(): number {
            return this.end.minutes - this.start.minutes;
        }

        get plain(): Plain.DayTimeSlice {
            return { start: this.start.plain, end: this.end.plain };
        }

        static from(plain: Plain.DayTimeSlice): DayTimeSlice {
            return plain instanceof DayTimeSlice ? plain : new DayTimeSlice(plain);
        }

        constructor(params: { startDate: Date; endDate: Date });
        constructor(params: { start: TimeOfDay; end: TimeOfDay });
        constructor(params: { start: TimeOfDay; minutes: number });
        constructor(params: Plain.DayTimeSlice);
        constructor(
            params:
                | { start: TimeOfDay; end: TimeOfDay }
                | { start: TimeOfDay; minutes: number }
                | { startDate: Date; endDate: Date }
                | Plain.DayTimeSlice,
        ) {
            if ('minutes' in params) {
                this.start = params.start;
                this.end = params.start.adding({ minutes: params.minutes });
            } else if ('start' in params) {
                this.start = params.start instanceof TimeOfDay ? params.start : new TimeOfDay(params.start);
                this.end = params.end instanceof TimeOfDay ? params.end : new TimeOfDay(params.end);
            } else {
                const daysUntilEnd = new SimpleDate(params.startDate).daysUntil(new SimpleDate(params.endDate));
                this.start = new TimeOfDay(params.startDate);
                this.end = new TimeOfDay(params.endDate).adding({ days: daysUntilEnd });
            }
        }

        contains(other: Plain.DayTimeSlice): boolean {
            const slice = DayTimeSlice.from(other);
            return slice.start.isAfterOrEqualTo(this.start) && slice.end.isBeforeOrEqualTo(this.end);
        }

        intersects(other: Plain.DayTimeSlice): boolean {
            const slice = DayTimeSlice.from(other);
            return this.start.isBefore(slice.end) && slice.start.isBefore(this.end);
        }

        intersecting(other: Plain.DayTimeSlice): DayTimeSlice | null {
            const slice = DayTimeSlice.from(other);
            const start = this.start.isAfter(slice.start) ? this.start : slice.start;
            const end = this.end.isBefore(slice.end) ? this.end : slice.end;
            if (start.isBefore(end)) {
                return new DayTimeSlice({ start, end });
            }
            return null;
        }
    }

    export class Weekday {
        static readonly sun = new Weekday(0);
        static readonly mon = new Weekday(1);
        static readonly tue = new Weekday(2);
        static readonly wed = new Weekday(3);
        static readonly thu = new Weekday(4);
        static readonly fri = new Weekday(5);
        static readonly sat = new Weekday(6);
        static readonly all: Weekday[] = [
            Weekday.sun,
            Weekday.mon,
            Weekday.tue,
            Weekday.wed,
            Weekday.thu,
            Weekday.fri,
            Weekday.sat,
        ];

        readonly index: number;

        private constructor(index: number) {
            this.index = index;
        }

        get name(): Plain.WeekdayName {
            switch (this.index) {
                case 0:
                    return 'sun';
                case 1:
                    return 'mon';
                case 2:
                    return 'tue';
                case 3:
                    return 'wed';
                case 4:
                    return 'thu';
                case 5:
                    return 'fri';
                case 6:
                    return 'sat';
                default:
                    return 'sun';
            }
        }

        static fromName(name: Plain.WeekdayName): Weekday {
            switch (name) {
                case 'sun':
                    return Weekday.sun;
                case 'mon':
                    return Weekday.mon;
                case 'tue':
                    return Weekday.tue;
                case 'wed':
                    return Weekday.wed;
                case 'thu':
                    return Weekday.thu;
                case 'fri':
                    return Weekday.fri;
                case 'sat':
                    return Weekday.sat;
            }
        }

        static fromIndex(index: number): Weekday {
            const normalized = ((index % 7) + 7) % 7;
            return Weekday.all[normalized];
        }

        isEqualTo(other: Weekday): boolean {
            return this.index === other.index;
        }

        nextDate(params?: { from?: Date }): SimpleDate {
            const base = params?.from ?? new Date();
            const baseDate = new Date(base.getFullYear(), base.getMonth(), base.getDate());
            const baseWeekday = baseDate.getDay();
            const delta = (this.index - baseWeekday + 7) % 7;
            baseDate.setDate(baseDate.getDate() + delta);
            return new SimpleDate(baseDate);
        }
    }
}
